import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";

import { useCart } from "../contexts/CartContext";
import { useSale } from "../contexts/SaleContext";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const MOBILE_BREAKPOINT = 500;
const PAGE_SIZE = 10;

function FeatureProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const swiperRef = useRef(null);
  const swiperInstance = useRef(null);

  const { addToCart } = useCart();
  const { sale } = useSale();

  const getDiscountedPrice = (retailPrice) => {
    if (sale.isActive && sale.discountPercent > 0 && retailPrice) {
      return Math.round(retailPrice * (1 - sale.discountPercent / 100));
    }
    return null;
  };

  useEffect(() => {
    const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const productList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching feature products:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Track window resize to switch between grid and slider
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Init/destroy Swiper when mobile mode or products change
  useEffect(() => {
    if (isMobile && swiperRef.current && products.length > 0) {
      swiperInstance.current = new Swiper(swiperRef.current, {
        modules: [Navigation, Pagination],
        loop: true,
        navigation: {
          prevEl: ".fp-prev",
          nextEl: ".fp-next",
        },
        pagination: {
          el: ".fp-pagination",
          clickable: true,
        },
        breakpoints: {
          0: { slidesPerView: 2, spaceBetween: 12 },
          420: { slidesPerView: 2, spaceBetween: 12 },
        },
      });
    }

    return () => {
      if (swiperInstance.current) {
        swiperInstance.current.destroy(true, true);
        swiperInstance.current = null;
      }
    };
  }, [isMobile, products]);

  const productCard = (product) => (
    <Link
      to={`/product/${product.id}`}
      className="product-card-link"
      key={product.id}
      style={{ textDecoration: "none", display: "flex", width: "100%", height: "100%" }}
    >
      <div className="product-card">
        <div className="card-image-box">
          {sale.isActive && sale.discountPercent > 0 && (
            <span className="discount-badge">{sale.discountPercent}% OFF</span>
          )}
          {product.isOutOfStock && (
            <span className="oos-overlay-badge">Out of Stock</span>
          )}

          <img src={product.picture} alt={product.name} />

          {!product.isOutOfStock && (
            <div className="add-to-cart-btn-wrapper">
              <button
                className="add-cart-btn"
                onClick={(e) => {
                  e.preventDefault();
                  const discountedPrice = getDiscountedPrice(product.retailPrice);
                  addToCart({ ...product, price: discountedPrice ?? product.price });
                }}
                title="Add to cart"
              >
                <FontAwesomeIcon icon={faCartShopping} />
              </button>
            </div>
          )}
        </div>

        <div className="card-info">
          <h3 className="product-type">{product.type}</h3>
          <h4 className="product-name">{product.name}</h4>

          <div className="price-container">
            {getDiscountedPrice(product.retailPrice) ? (
              <>
                <span className="new-price">Rs. {getDiscountedPrice(product.retailPrice)}</span>
                <span className="old-price">Rs. {product.retailPrice}</span>
              </>
            ) : (
              <span className="new-price">Rs. {product.price}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="product-section" data-aos="fade-up">

      {/* Header */}
      <div className="deals-header">
        <h2 className="deals-title">
          Feature <span className="big-span highlight">Products</span>
        </h2>
        <Link to="/products" className="see-all">
          <p>See All</p>
          <svg
            width="15"
            height="13"
            viewBox="0 0 15 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.8223 7.10248C14.1659 6.75882 14.1659 6.20163 13.8223 5.85797L8.22197 0.257686C7.87831 -0.0859759 7.32112 -0.085976 6.97746 0.257686C6.6338 0.601347 6.6338 1.15853 6.97746 1.50219L11.9555 6.48023L6.97746 11.4583C6.6338 11.8019 6.6338 12.3591 6.97746 12.7028C7.32112 13.0464 7.87831 13.0464 8.22197 12.7028L13.8223 7.10248ZM0 6.48022L0 7.36022L13.2 7.36023L13.2 6.48023L13.2 5.60023L0 5.60022L0 6.48022Z"
              fill="#D2222D"
            />
          </svg>
        </Link>
      </div>

      {loading ? (
        /* Skeleton state — same layout, shimmer cards */
        isMobile ? (
          <div className="slider-nav-wrapper">
            <button className="slider-nav-btn fp-prev" aria-label="Previous" disabled>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13H6.75L12 18.25l-.66.75l-6.5-6.5l6.5-6.5l.66.75L6.75 12H19z" /></svg>
            </button>
            <div className="swiper fp-swiper">
              <div className="swiper-wrapper">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div className="swiper-slide shrink-slide" key={i}>
                    <div className="product-card skeleton-card">
                      <div className="skeleton-image-box skeleton-shimmer" />
                      <div className="skeleton-info">
                        <div className="skeleton-line skeleton-shimmer" style={{ width: "50%", height: "12px" }} />
                        <div className="skeleton-line skeleton-shimmer" style={{ width: "80%", height: "16px", marginTop: "8px" }} />
                        <div className="skeleton-line skeleton-shimmer" style={{ width: "40%", height: "14px", marginTop: "10px" }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button className="slider-nav-btn fp-next" aria-label="Next" disabled>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M4 12h12.25L11 6.75l.66-.75l6.5 6.5l-6.5 6.5l-.66-.75L16.25 13H4z" /></svg>
            </button>
          </div>
        ) : (
          <div className="products-grid">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div className="product-card skeleton-card" key={i}>
                <div className="skeleton-image-box skeleton-shimmer" />
                <div className="skeleton-info">
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "50%", height: "12px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "80%", height: "16px", marginTop: "8px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "40%", height: "14px", marginTop: "10px" }} />
                </div>
              </div>
            ))}
          </div>
        )
      ) : isMobile ? (
        <div className="slider-nav-wrapper">
          <button className="slider-nav-btn fp-prev" aria-label="Previous">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M19 13H6.75L12 18.25l-.66.75l-6.5-6.5l6.5-6.5l.66.75L6.75 12H19z" />
            </svg>
          </button>
          <div className="swiper fp-swiper" ref={swiperRef}>
            <div className="swiper-wrapper">
              {products.map((product) => (
                <div className="swiper-slide shrink-slide" key={product.id}>
                  {productCard(product)}
                </div>
              ))}
            </div>
          </div>
          <button className="slider-nav-btn fp-next" aria-label="Next">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M4 12h12.25L11 6.75l.66-.75l6.5 6.5l-6.5 6.5l-.66-.75L16.25 13H4z" />
            </svg>
          </button>
        </div>
      ) : (
        /* Desktop/Tablet: Grid with Load More */
        <>
          <div className="products-grid">
            {products.slice(0, visibleCount).map((product) => productCard(product))}
          </div>
          {visibleCount < products.length && (
            <div className="load-more-wrapper">
              <button
                className="load-more-btn"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}

    </div>
  );
}

export default FeatureProducts;
